import type { StatusRole } from '@lighthouse/design'
import { NOT_MEASURED, exactNumber, shortDuration } from '../../utils/format'
import type { EtlConnection, EtlConnectionOutcome } from '../../types/observatory'

/**
 * Presentation for the connection ledger.
 *
 * Every helper here is a wording decision, not a calculation. The endpoint
 * already applies every filter, orders by `Sequence` (not `StartedAt` — two
 * attempts opening in the same second are a tie the whole-second column cannot
 * break), pages, counts, and measures `oldestRetainedAt` off the whole table.
 * **Nothing here recomputes any of them**, and in particular nothing here
 * filters, sorts or totals a page: a page is 200 rows out of ~6,300, so a
 * client-side aggregate over one would be a window of the answer wearing the
 * answer's clothes.
 *
 * Two deliberate exceptions, named so the fence stays legible:
 *
 *  - {@link connectionLifetime} subtracts two served instants to say how long a
 *    connection was held. It is arithmetic, but over one row's own two fields,
 *    and it is the sentence the pair exists to support.
 *  - {@link retryDepth} reads `attemptNumber`, which the server sends. It
 *    classifies rather than counts — nothing is summed across rows.
 */

/**
 * The outcome's status role, or `null` for one that has not finished.
 *
 * **`running` gets no role, deliberately.** It is not good news and it is not
 * bad news; it is the absence of news, and the badge vocabulary has no member
 * for that — which is the same reason `status` is nullable on the source-health
 * contract. Giving an in-flight connection a colour would make a page of live
 * work read as a page of verdicts.
 *
 * `abandoned` is `warning` rather than `critical` on purpose. Nothing observed
 * it fail: a later attempt found it still open and stamped it, because the
 * worker holding it never came back. Painting that red claims the connection
 * broke, when what is actually known is that nobody ever said.
 */
export function outcomeRole(outcome: EtlConnectionOutcome): StatusRole | null {
  switch (outcome) {
    case 'succeeded': return 'good'
    case 'failed': return 'critical'
    case 'abandoned': return 'warning'
    default: return null
  }
}

/** The outcome as a caption, including for the one that has no badge. */
export function outcomeLabel(outcome: EtlConnectionOutcome): string {
  switch (outcome) {
    case 'succeeded': return 'Succeeded'
    case 'failed': return 'Failed'
    case 'abandoned': return 'Abandoned'
    default: return 'Running'
  }
}

/**
 * What `abandoned` actually means, which nobody guesses right.
 *
 * Returned as a caption rather than a tooltip: it is the one outcome whose name
 * misleads, since it sounds like a decision somebody made and is in fact the
 * absence of one.
 */
export function outcomeGloss(outcome: EtlConnectionOutcome): string | null {
  switch (outcome) {
    case 'abandoned':
      return 'Stamped by a later attempt that found this one still open. The worker holding it never '
        + 'came back to say what happened, so its timestamps stop where that worker did.'
    case 'running':
      return 'Still open, as of this page load. Nothing has reported an outcome yet.'
    default:
      return null
  }
}

/**
 * How long the source connection was held — **the field pair, read correctly.**
 *
 * This is the page's central trap, and it is worth the length. `closedAt` being
 * null does NOT mean the attempt failed: an attempt lands first and transforms
 * second, and only the landing closes the connection, so one that landed and
 * then died in the transform keeps a real close instant. Null means exactly two
 * things — still landing now, or lost before the landing finished — and
 * `landingMs` is what tells them apart, because it is recorded whether or not
 * the connection closed cleanly.
 *
 * So the three readings are:
 *
 *  - duration and a close  → held it that long and let it go
 *  - duration and no close → held it that long and then LOST it
 *  - no duration           → nothing was measured; say so rather than infer
 *
 * The outcome is deliberately not an input. Pairing this with `outcome` is the
 * mistake the wire contract was corrected for: `failed` beside a populated
 * `closedAt` is the ordinary shape of every transform-phase failure, not a
 * contradiction, and on the two ~100M-row windowed entities it is the common
 * case.
 */
export function connectionLifetime(connection: EtlConnection): string {
  const { landingMs, closedAt } = connection
  if (landingMs == null) return NOT_MEASURED
  const held = shortDuration(landingMs / 1000)
  return closedAt == null ? `${held}, then lost` : held
}

/** Milliseconds as a duration, or a dash. Never zero-for-unmeasured. */
export function msLabel(ms: number | null): string {
  if (ms == null) return NOT_MEASURED
  if (ms < 1000) return `${exactNumber(ms)} ms`
  return shortDuration(ms / 1000)
}

/**
 * Whether this row is a retry, and how deep.
 *
 * `attemptNumber` is 1-based, so 2 means attempt 1 did not finish. Surfacing it
 * per row is most of the point of the table existing: before the attempt table,
 * three failed attempts and a success left ONE row saying "succeeded", and the
 * failures an operator most wants were the ones being overwritten.
 */
export function retryDepth(attemptNumber: number): string | null {
  if (attemptNumber <= 1) return null
  return `Attempt ${attemptNumber}`
}

/**
 * The slice this connection was asked for.
 *
 * Rendered as plain dates with NO timezone conversion, unlike every other
 * timestamp on this page. Oracle's `LAST_UPDATE_DATE` carries no timezone, so
 * the window is a naive source-frame date carried unconverted from end to end —
 * running it through `toLocaleString()` would shift a boundary by the viewer's
 * offset and show a window the ETL never asked for.
 */
export function windowLabel(windowStart: string, windowEnd: string): string {
  return `${naiveDate(windowStart)} → ${naiveDate(windowEnd)}`
}

/** The date part as written, before any timezone is applied to it. */
function naiveDate(iso: string): string {
  return iso.slice(0, 10)
}

/**
 * "Rows landed", which is the one volume figure that belongs to this connection.
 *
 * `rowsTransformed` deliberately has no helper here and is labelled at the call
 * site as what it is: the transform procedure takes no chunk id and drains the
 * landing table wholesale, so it legitimately includes rows landed by another
 * chunk or another run. Any helper named for a possessive reading of it would be
 * false, so there is none to reach for.
 */
export function rowsLandedLabel(rows: number): string {
  return exactNumber(rows)
}

/**
 * How far back the ledger actually reaches, as a sentence.
 *
 * Shown whether or not anything is filtered, because it answers a question the
 * list cannot: the difference between "no connection before this date failed"
 * and "no connection before this date is still on file". A reader who cannot
 * tell those apart will read a retention boundary as a clean bill of health.
 */
export function retentionLabel(oldestRetainedAt: string | null): string {
  if (oldestRetainedAt == null) {
    return 'Nothing has ever been recorded here, so there is no retention boundary yet.'
  }
  return `Oldest connection still on file: ${new Date(oldestRetainedAt).toLocaleString()}. `
    + 'Anything older has been trimmed — its absence is not evidence that it did not happen.'
}

/**
 * Whether a requested lower bound reaches past what is actually retained.
 *
 * A filter for "the last 60 days" against a 30-day ledger returns 30 days and
 * looks like a complete answer to the question asked. This is the only
 * comparison on the page, it is between two served values, and it exists to
 * refuse exactly that reading.
 */
export function reachesPastRetention(from: string | null, oldestRetainedAt: string | null): boolean {
  if (from == null || oldestRetainedAt == null) return false
  return new Date(from).getTime() < new Date(oldestRetainedAt).getTime()
}
